import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@screentone/addon-auth-wrapper';
import {
  Box,
  Button,
  Divider,
  FormLabel,
  Group,
  Overlay,
  Switch,
  Typography,
  useModalPortal,
  Wrapper
} from '@screentone/core';
import { merge } from 'lodash';
import debounce from 'lodash.debounce';
import { v4 as uuidv4 } from 'uuid';

import { ResponsiveLoader } from 'components';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { Article } from 'components/datamodel/content/Article/Article';
import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { QueryRulesForm } from 'components/query-rules-form/QueryRulesForm';
import {
  DEFAULT_COUNT,
  getQueryRulesFromAllessehQueryTerms,
  QueryRuleFilterKey,
  QueryRuleFlags
} from 'components/query-rules-form/queryRulesFormUtils';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { MenuActionsKeys } from 'contexts/context-menu-actions/ContextMenuActionsContext';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useContextMenuActions } from 'contexts/context-menu-actions/useContextMenuActions';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { QueryItem } from 'data/generated/graphql';
import { usePublicationSettings } from 'hooks/publication-settings';
import {
  AllessehContent,
  AllessehContentQueryBody,
  AllessehQueryRule,
  useAllessehContentQuery
} from 'hooks/useAllessehContentQuery';
import { useAllessehDedupedQuery } from 'hooks/useAllessehDedupedQuery';
import { getExcludedIdsQuery, mergeAllessehQueryBodies, validateValue } from 'utils/queryUtils';
import { safelyParseContent } from 'utils/temp';
import styles from './QueryModal.module.scss';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface QueryModalProps {
  queryId: string;
  queryItemHierarchyId: string;
  onDismiss: () => void;
  allessehJsonQuery: string;
  onChangeJsonQueryStr: (newJsonQueryStr: string) => void;
  count: number | null;
  baseQuery: string;
  ignoreBaseQuery?: boolean;
  viewOnly?: boolean;
  queryRuleDisabledFlags?: QueryRuleFlags;
  queryRuleHiddenFlags?: QueryRuleFlags;
  queryRuleFilterKey?: QueryRuleFilterKey;
}

export const QueryModal = ({
  queryId,
  queryItemHierarchyId,
  allessehJsonQuery,
  onDismiss,
  onChangeJsonQueryStr,
  count,
  baseQuery,
  ignoreBaseQuery = false,
  viewOnly,
  queryRuleDisabledFlags,
  queryRuleHiddenFlags,
  queryRuleFilterKey
}: QueryModalProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { renderNode } = useModalPortal();
  const [jsonQueryStr, setJsonQueryStr] = useState(allessehJsonQuery);
  const [allessehQueryBody, setAllessehQueryBody] = useState<AllessehContentQueryBody | null>(null);
  const [bypassBaseQuery, setBypassBaseQuery] = useState(ignoreBaseQuery);
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { currentProperty } = useAuth();
  const { getDTO, traverseEntity, modifyEntity } = useDataModelContext();
  const { renderActions: renderMenuActions } = useContextMenuActions();

  const isBypassToggleDisabled = useMemo(() => ignoreBaseQuery, [ignoreBaseQuery]);
  const baseQueryRules = useMemo(
    () => (bypassBaseQuery ? null : getQueryRulesFromAllessehQueryTerms(baseQuery)),
    [baseQuery, bypassBaseQuery]
  );
  const disabledFlags = useMemo(
    () => merge({}, baseQueryRules, queryRuleDisabledFlags),
    [baseQueryRules, queryRuleDisabledFlags]
  );

  const artifactDTO = useMemo(() => {
    const currentDTO = getDTO(true);

    // Traverse the DTO to find the query item with the given ID
    // Then, update its query body with the new allessehQueryBody
    traverseEntity(currentDTO.root, (entity) => {
      if (entity.type === EntityType.Query) {
        const queryItem = entity as QueryItem;
        if (allessehQueryBody && queryItem.attributes.id === queryId) {
          queryItem.attributes.query = allessehQueryBody as Record<string, unknown>;
        }
      }
    });

    return currentDTO;
  }, [allessehQueryBody, getDTO, queryId, traverseEntity]);

  const updateAllessehQueryBody = (allessehJsonQuery?: string) => {
    const fullAllessehQuery = mergeAllessehQueryBodies(ignoreBaseQuery ? '{}' : baseQuery, allessehJsonQuery);
    setAllessehQueryBody(fullAllessehQuery);
  };
  const debouncedUpdate = debounce(updateAllessehQueryBody, 500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDebouncedQueryBody = useCallback(debouncedUpdate, [publicationSettingsResp]);

  useEffect(() => {
    setJsonQueryStr(allessehJsonQuery);
  }, [allessehJsonQuery]);

  useEffect(() => {
    if (!allessehQueryBody) {
      updateAllessehQueryBody(jsonQueryStr);
    } else {
      updateDebouncedQueryBody(jsonQueryStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonQueryStr]);

  const {
    data: articles,
    isLoading: isLoad,
    fetchStatus
  } = useAllessehDedupedQuery(artifactDTO, queryId, { enabled: !!artifactDTO });

  // fetch the data needed to display the headlines of excluded articles
  const excludedAllessehContentQuery = getExcludedIdsQuery(allessehQueryBody);
  const fullExcludedContentAllessehQuery = mergeAllessehQueryBodies(
    ignoreBaseQuery ? '{}' : baseQuery,
    JSON.stringify(excludedAllessehContentQuery)
  );
  const { data: excludedData } = useAllessehContentQuery(fullExcludedContentAllessehQuery, {
    enabled: !!fullExcludedContentAllessehQuery,
    publicationKey: publicationSettingsResp?.publicationSetting.publicationKey
  });
  const excludedArticles = excludedData?.pages[0]?.data?.attributes;

  const isLoading = isLoad && fetchStatus !== 'idle';

  const noResultsMessage = validateValue(
    currentProperty,
    allessehQueryBody?.query as { [key: string]: AllessehQueryRule[] },
    publicationSettingsResp?.publicationSetting.queryRuleSchema
  );

  const handleAllessehJsonQueryChange = (newAllessehJsonQuery: string, fromClear: boolean = false) => {
    const parsedQuery = fromClear
      ? mergeAllessehQueryBodies(baseQuery, newAllessehJsonQuery)
      : (JSON.parse(newAllessehJsonQuery) as AllessehContentQueryBody);
    parsedQuery.count = count ?? DEFAULT_COUNT;
    setJsonQueryStr(JSON.stringify(parsedQuery));
  };

  const handleCancel = () => {
    onDismiss();
  };

  const handleSave = () => {
    const parsedJsonQuery = JSON.parse(jsonQueryStr) as AllessehContentQueryBody;
    onChangeJsonQueryStr(JSON.stringify({ ...parsedJsonQuery, count }));

    modifyEntity(queryItemHierarchyId, (entity) => {
      const query = entity as QueryItem;
      if (!query.attributes.id) {
        query.attributes.id = uuidv4();
      }
    });

    onDismiss();
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    <Overlay onDismiss={onDismiss} status="open" renderNode={renderNode} className={styles.overlay}>
      <Box>
        <Box.Title>QUERY {viewOnly ? 'VIEWER' : 'BUILDER'}</Box.Title>
        <Box.Content padding={{ all: 'none' }}>
          <Wrapper className={styles.content} padding={{ top: 'sm', right: 'md', bottom: 'md', left: 'md' }}>
            <Wrapper className={styles.contentLeft} padding={{ right: 'md' }}>
              <QueryRulesForm
                allessehJsonQuery={jsonQueryStr}
                excludedData={excludedArticles}
                fullAllessehJsonQueryOverride={allessehQueryBody}
                onAllessehJsonQueryChange={!viewOnly ? handleAllessehJsonQueryChange : null}
                onQueryRuleChange={!viewOnly ? handleAllessehJsonQueryChange : null}
                queryRuleDisabledFlags={disabledFlags}
                queryRuleHiddenFlags={queryRuleHiddenFlags}
                queryRuleFilterKey={queryRuleFilterKey}
              />
            </Wrapper>
            <div className={styles.contentRight}>
              <Typography size="lg" margin={{ bottom: 'sm', top: 'sm' }}>
                Query Results
              </Typography>
              <Wrapper>
                {isLoading && <ResponsiveLoader />}
                {!isLoading && articles?.length === 0 && (
                  <>
                    <NoSearchResults title="Results" bodyText={noResultsMessage} />
                  </>
                )}
                {!isLoading && (articles?.length ?? 0) > 0 && (
                  <div className={styles.contentList}>
                    {articles?.map((articleItem, i) => (
                      <Article
                        key={i}
                        data={articleItem}
                        renderActions={() =>
                          renderMenuActions(MenuActionsKeys.ArticleQueryModal, {
                            hierarchyId: queryItemHierarchyId,
                            entity: articleItem,
                            isHistory: viewOnly,
                            extraProperties: {
                              allessehContent: safelyParseContent<AllessehContent>(articleItem.content),
                              jsonQueryStr,
                              setJsonQueryStr
                            }
                          })
                        }
                        isDraft={!viewOnly}
                        parentQueryHierarchyId={queryItemHierarchyId}
                        tooltipInPortal={false}
                      />
                    ))}
                  </div>
                )}
              </Wrapper>
            </div>
          </Wrapper>
          <Divider />
            <Wrapper padding={{ all: 'md' }}>
              <Group gap="md" align="end" valign="center">
                {!viewOnly && (
                  <FormLabel label="Bypass base query" labelPosition="right">
                    <Switch
                      name="bypass-base-query"
                      checked={bypassBaseQuery}
                      onChange={() => setBypassBaseQuery(!bypassBaseQuery)}
                      disabled={isBypassToggleDisabled}
                    />
                  </FormLabel>
                )}
                <Button secondary onClick={handleCancel} data-testid="page-query-cancel">
                  Cancel
                </Button>
                <Button primary onClick={handleSave} data-testid="page-query-save" disabled={viewOnly}>
                  Save
                </Button>
              </Group>
            </Wrapper>
        </Box.Content>
      </Box>
    </Overlay>
  );
};
